<template>
  <div class="neumorphism-out list">
    <div class="list-title space-between">
      <div class="mr-2">
        {{ $t("crudRooms.title") }}
      </div>

      <div>
        <v-tooltip bottom>
          <template #activator="{ on }">
            <EditRoomDialogButton
              :center="center"
              :room="newRoom"
              @closed="clearNewRoom"
              @saved="resizeRoom"
              icon
              small
              v-on="on"
            >
              <template #dialog-button-text>
                <v-icon> mdi-plus-circle </v-icon>
              </template>
            </EditRoomDialogButton>
          </template>
          <span>{{ $t("roomsList.tooltipAdd") }}</span>
        </v-tooltip>

        <v-tooltip bottom>
          <template #activator="{ on }">
            <v-btn icon small @click="saveRooms" v-on="on">
              <v-icon>mdi-content-save</v-icon>
            </v-btn>
          </template>
          <span>{{ $t("roomsList.tooltipSaveAll") }}</span>
        </v-tooltip>

        <v-tooltip bottom>
          <template #activator="{ on }">
            <v-btn icon small @click="resizeRooms()" v-on="on">
              <v-icon>mdi-resize</v-icon>
            </v-btn>
          </template>
          <span>{{ $t("roomsList.tooltipResizeAll") }}</span>
        </v-tooltip>
      </div>
    </div>
    <div>
      <v-dialog v-model="deleteRoomDialogOpen" max-width="500px">
        <v-card class="card-delete">
          <v-card-title>{{ $t("crudRooms.deleteConfirm") }}</v-card-title>

          <v-card-actions>
            <v-spacer />
            <v-btn color="F44336" text @click="closeRoomDeleteDialog">
              {{ $t("crudCommon.cancel") }}
            </v-btn>
            <v-btn color="F44336" text @click="confirmRoomDeleteDialog">
              {{ $t("crudCommon.delete") }}
            </v-btn>
            <v-spacer />
          </v-card-actions>
        </v-card>
      </v-dialog>

      <div class="list-content">
        <ul
          v-for="room in sortRooms"
          :key="room.id"
          class="neumorphism-in list-item"
        >
          <li class="space-between">
            <div
              class="elem-text"
              style="cursor: pointer"
              @click="goToRoomDetails(room)"
            >
              {{ room.name }}
            </div>
            <div>
              <v-tooltip bottom>
                <template #activator="{ on }">
                  <EditRoomDialogButton
                    :center="room.center"
                    :room="roomCopy"
                    @click="copyRoomOf(room)"
                    icon
                    x-small
                    v-on="on"
                  >
                    <template #dialog-button-text>
                      <v-icon> mdi-pencil </v-icon>
                    </template>
                  </EditRoomDialogButton>
                </template>
                <span>{{ $t("crudCommon.tooltipEdit") }}</span>
              </v-tooltip>

              <v-tooltip bottom>
                <template #activator="{ on }">
                  <v-btn
                    icon
                    x-small
                    @click="openRoomDeleteDialog(room)"
                    v-on="on"
                  >
                    <v-icon>mdi-delete</v-icon>
                  </v-btn>
                </template>
                <span>{{ $t("crudCommon.tooltipDelete") }}</span>
              </v-tooltip>

              <v-tooltip bottom>
                <template #activator="{ on }">
                  <v-btn icon x-small @click="resizeRoom(room)" v-on="on">
                    <v-icon>mdi-resize</v-icon>
                  </v-btn>
                </template>
                <span>{{ $t("roomsList.tooltipResize") }}</span>
              </v-tooltip>

              <v-tooltip bottom>
                <template #activator="{ on }">
                  <v-btn icon x-small @click="bringRoomToFront(room)" v-on="on">
                    <v-icon>mdi-arrange-bring-to-front</v-icon>
                  </v-btn>
                </template>
                <span>{{ $t("roomsList.tooltipBringToFront") }}</span>
              </v-tooltip>
            </div>
          </li>
        </ul>
      </div>
    </div>
  </div>
</template>

<script>
import EditRoomDialogButton from "@/components/components-salles/admin/crud/room/EditRoomDialogButton";
import notif from "@/services/common/NotificationHelperService";
import i18n from "@/i18n";
import { mapActions } from "vuex";

export default {
  name: "RoomsList",
  components: {
    EditRoomDialogButton,
  },
  props: {
    center: null,
    rooms: null,
    planRef: null,
  },
  computed: {
    sortRooms() {
      let sortedSlots = this.rooms;
      sortedSlots = sortedSlots.sort((a, b) => {
        let A = a.name,
          B = b.name;
        if (A < B) {
          return -1;
        }
        if (A > B) {
          return 1;
        }
        return 0;
      });
      return sortedSlots;
    },
  },
  data: () => ({
    newRoom: {},
    roomCopy: null,
    deleteRoomDialogOpen: false,
    roomToDelete: null,
  }),
  methods: {
    ...mapActions("roomModule", [
      "editRoom",
      "deleteRoom",
      "showRoomStoreNotifications",
    ]),
    ...mapActions("notificationModule", ["showNotification"]),

    clearNewRoom() {
      this.newRoom = {};
    },
    copyRoomOf(room) {
      this.roomCopy = structuredClone(room);
    },
    openRoomDeleteDialog(room) {
      this.roomToDelete = room;
      this.deleteRoomDialogOpen = true;
    },
    closeRoomDeleteDialog() {
      this.deleteRoomDialogOpen = false;
    },
    confirmRoomDeleteDialog() {
      this.deleteRoom(this.roomToDelete);
      this.closeRoomDeleteDialog();
    },
    resizeRoom(room) {
      room.x = 0;
      room.y = 0;
      room.w = 60;
      room.h = 60;
      this.planRef.requestCanvasFrame();
    },
    resizeRooms() {
      for (let room of this.rooms) {
        room.x = 0;
        room.y = 0;
        room.w = 60;
        room.h = 60;
      }
      this.planRef.requestCanvasFrame();
    },
    bringRoomToFront(room) {
      this.planRef.bringToFront(room);
    },
    async saveRooms() {
      try {
        this.showRoomStoreNotifications(false);
        try {
          for (let room of this.rooms) await this.editRoom(room);
          notif.showSuccess(
            this.$store.dispatch,
            i18n.t("crudRooms.planSaveAllSuccess")
          );
        } catch (e) {
          notif.showError(
            this.$store.dispatch,
            i18n.t("crudRooms.planSaveAllError")
          );
        }
      } finally {
        this.showRoomStoreNotifications(true);
      }
    },
    goToRoomDetails(item) {
      this.$router.push({
        name: "admin-details-room",
        params: {
          centerId: item.centerId,
          roomId: item.id,
        },
      });
    },
  },
};
</script>

<style lang="scss" scoped>
.list {
  border-radius: 5px;
}

.list-title {
  margin: 5px;
  padding: 0px 10px;
}

.list-content {
  max-height: 270px;
  overflow: scroll;
}

.list-item {
  user-select: none;
  padding: 5px 10px;
  margin: 5px 10px;
  border-radius: 5px;
  align-content: center;
  justify-content: center;
  list-style: none;
}

.list-item-title {
  max-width: 200px;
  overflow: hidden;
  text-overflow: ellipsis;
}

.space-between {
  display: flex;
  justify-content: space-between;
  align-items: center;
}
</style>
